'use client'

import React, { useState } from 'react'
import { CalendarDays, Phone } from 'lucide-react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { cn } from '@/lib/utils'
import { RecaptchaProvider } from '@/components/shared/RecaptchaProvider'
import {
  Field,
  FormError,
  FormSuccess,
  SelectInput,
  SubmitButton,
  TextArea,
  TextInput,
} from '@/components/site/fields'

const MODES = [
  { key: 'demo', label: 'Demo buchen', icon: CalendarDays },
  { key: 'callback', label: 'Rückruf anfordern', icon: Phone },
]

export function DemoForm() {
  return (
    <RecaptchaProvider>
      <DemoFormInner />
    </RecaptchaProvider>
  )
}

function DemoFormInner() {
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [mode, setMode] = useState('demo') // 'demo' | 'callback'
  const [status, setStatus] = useState('idle') // idle | loading | success | error
  // A sub-second fill is a bot, not a person.
  const shownAt = React.useRef(Date.now())
  const callback = mode === 'callback'

  const handleSubmit = async e => {
    e.preventDefault()
    setStatus('loading')
    const form = e.currentTarget
    const data = Object.fromEntries(new FormData(form).entries())

    try {
      const recaptchaToken = executeRecaptcha
        ? await executeRecaptcha(callback ? 'lead_callback' : 'lead_demo')
        : undefined
      const res = await fetch('/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          type: mode,
          formTime: Date.now() - shownAt.current,
          source_page: '/demo',
          recaptcha_token: recaptchaToken,
        }),
      })
      if (!res.ok)
        throw new Error((await res.json().catch(() => ({})))?.error || 'error')
      setStatus('success')
      form.reset()
    } catch {
      setStatus('error')
    }
  }

  if (status === 'success') {
    return (
      <FormSuccess
        title="Vielen Dank!"
        action={
          <button
            type="button"
            onClick={() => setStatus('idle')}
            className="mt-2 text-[15px] font-semibold text-primary underline-offset-4 hover:underline"
          >
            Weitere Anfrage senden
          </button>
        }
      >
        {callback
          ? 'Wir haben Ihre Rückruf-Anfrage erhalten und melden uns innert 24 Stunden bei Ihnen.'
          : 'Wir haben Ihre Demo-Anfrage erhalten und melden uns innert 24 Stunden, um einen Termin zu vereinbaren.'}
      </FormSuccess>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <div
        role="group"
        aria-label="Art der Anfrage"
        className="grid grid-cols-2 rounded-control border border-rule-strong bg-canvas p-0.5"
      >
        {MODES.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            aria-pressed={mode === key}
            onClick={() => setMode(key)}
            className={cn(
              'flex h-10 items-center justify-center gap-2 rounded-[3px] text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal',
              mode === key
                ? 'bg-surface text-ink shadow-[0_1px_2px_rgb(var(--ink)/0.12)]'
                : 'text-graphite hover:text-ink'
            )}
          >
            <Icon aria-hidden className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-1">
        <h2 className="text-2xl font-[450] text-ink [font-stretch:110%]">
          {callback ? 'Wir rufen Sie zurück' : 'Demo anfragen'}
        </h2>
        <p className="text-[15px] text-graphite">
          {callback
            ? 'Kurz Nummer hinterlassen, wir melden uns innert 24 Stunden.'
            : 'Persönliche Produkt-Tour, kostenlos und unverbindlich.'}
        </p>
      </div>

      <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
        {/* Honeypot (hidden from users, bots fill it) */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          className="hidden"
          aria-hidden="true"
        />

        <Field id="name" label="Name" required>
          <TextInput
            id="name"
            name="name"
            required
            autoComplete="name"
            placeholder="Max Muster"
          />
        </Field>

        <Field id="phone" label="Telefon" required={callback}>
          <TextInput
            id="phone"
            name="phone"
            type="tel"
            required={callback}
            autoComplete="tel"
            placeholder="+41 79 000 00 00"
          />
        </Field>

        {!callback && (
          <>
            <Field id="email" label="E-Mail" required>
              <TextInput
                id="email"
                name="email"
                type="email"
                required
                autoComplete="email"
                placeholder="max@firma.ch"
              />
            </Field>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field id="company" label="Firma">
                <TextInput
                  id="company"
                  name="company"
                  autoComplete="organization"
                  placeholder="Muster Verwaltung AG"
                />
              </Field>
              <Field id="portfolio_size" label="Anzahl Einheiten">
                <SelectInput
                  id="portfolio_size"
                  name="portfolio_size"
                  defaultValue=""
                >
                  <option value="" disabled>
                    Bitte wählen
                  </option>
                  <option value="1-20">1–20</option>
                  <option value="21-50">21–50</option>
                  <option value="51-150">51–150</option>
                  <option value="151-500">151–500</option>
                  <option value="500+">500+</option>
                </SelectInput>
              </Field>
            </div>
          </>
        )}

        <Field id="preferred_time" label="Bestes Zeitfenster">
          <SelectInput
            id="preferred_time"
            name="preferred_time"
            defaultValue=""
          >
            <option value="" disabled>
              Bitte wählen
            </option>
            <option value="Vormittag (08–12 Uhr)">Vormittag (08–12 Uhr)</option>
            <option value="Nachmittag (12–17 Uhr)">
              Nachmittag (12–17 Uhr)
            </option>
            <option value="Abend (17–19 Uhr)">Abend (17–19 Uhr)</option>
          </SelectInput>
        </Field>

        {!callback && (
          <Field id="message" label="Nachricht (optional)">
            <TextArea
              id="message"
              name="message"
              placeholder="Worauf sollen wir in der Demo besonders eingehen?"
            />
          </Field>
        )}

        {status === 'error' && (
          <FormError>
            Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut oder rufen
            Sie uns an: +41 79 133 22 22.
          </FormError>
        )}

        <SubmitButton loading={status === 'loading'}>
          {callback ? 'Rückruf anfordern' : 'Demo anfragen'}
        </SubmitButton>

        <p className="text-center text-xs text-graphite">
          Mit dem Absenden stimmen Sie unserer{' '}
          <a
            href="/datenschutz"
            className="underline underline-offset-2 hover:text-ink"
          >
            Datenschutzerklärung
          </a>{' '}
          zu.
        </p>
      </form>
    </div>
  )
}
